import React, { useState } from 'react';
import { X, Calendar, CheckCircle, Clock, ShoppingCart, Copy, Check, Edit2, Save, AlertCircle } from 'lucide-react';

function ProductDetailModal({ item, onClose, handleSellOne, token, activeShopId, onUpdate }) {
  const [copiedField, setCopiedField] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrice, setEditedPrice] = useState(item?.sale_price || 0);
  const [editedCost, setEditedCost] = useState(item?.cost_price || 0);

  if (!item) return null;

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSave = async () => {
    try {
      const shopParam = activeShopId ? `&shopId=${activeShopId}` : "";
      const res = await fetch(`/api/inventory/fix-price?t=${Date.now()}${shopParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: item.id,
          sale_price: Number(editedPrice),
          cost_price: Number(editedCost)
        })
      });
      if (res.ok) {
        setIsEditing(false);
        if (onUpdate) onUpdate();
        // Since we can't easily update the parent state from here without complex props,
        // we might just close or rely on the next refresh.
        alert("Saqlandi");
      }
    } catch (e) { alert("Xato!"); }
  };

  const fullCode = item.marking_code || '';

  return (
    <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-2xl z-[70] flex items-center justify-center p-4 sm:p-8 overflow-y-auto">
      <div className="bg-white rounded-[50px] w-full max-w-4xl overflow-hidden shadow-2xl scale-in my-auto">
        <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-blue-500 p-8 sm:p-12 flex justify-between items-center relative">
          <div className="text-white flex-1 min-w-0 pr-4">
            <h4 className="text-[10px] font-black uppercase tracking-[0.4em] opacity-70 mb-2 text-indigo-100">Raqamli Mahsulot Pasporti</h4>
            <div className="text-xl sm:text-2xl font-black leading-snug drop-shadow-lg break-words">{item.product_name}</div>
          </div>
          <button onClick={onClose} className="p-4 bg-black/10 text-white rounded-[25px] hover:bg-black/20 transition backdrop-blur-lg border border-white/10 shrink-0"><X size={32}/></button>
        </div>

        <div className="p-8 sm:p-12 space-y-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left Column: Code Visualization */}
            <div className="bg-slate-950 p-8 rounded-[40px] border border-blue-500/20 shadow-inner flex flex-col items-center justify-center space-y-8">
               <div className="bg-white p-6 rounded-[35px] shadow-[0_0_50px_rgba(59,130,246,0.3)]">
                  <img src={`/api/barcode/${encodeURIComponent(item.marking_code)}`} alt="Barcode" className="w-56 h-56 object-contain" />
               </div>

               <div className="w-full space-y-4">
                  <div className="bg-blue-500/5 border border-blue-500/10 p-6 rounded-[25px] group relative overflow-hidden">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-[9px] font-black text-blue-500 uppercase tracking-[0.2em]">Markirovka Kodi (To'liq)</p>
                      <button onClick={() => copyToClipboard(fullCode, 'full')} className="p-2 bg-blue-500/10 text-blue-400 rounded-xl hover:bg-blue-500 hover:text-white transition">
                        {copiedField === 'full' ? <Check size={16}/> : <Copy size={16}/>}
                      </button>
                    </div>
                    <p className="text-xs font-mono text-blue-400 break-all leading-relaxed tracking-wider font-bold">{fullCode}</p>
                  </div>
               </div>
            </div>

            {/* Right Column: Details & Editing */}
            <div className="flex flex-col justify-between space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div className="p-6 bg-slate-50 rounded-[30px] border border-slate-100 group">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Tannarx (Dona)</p>
                    <button onClick={() => setIsEditing(!isEditing)} className="text-blue-500 opacity-0 group-hover:opacity-100 transition"><Edit2 size={14}/></button>
                  </div>
                  {isEditing ? (
                    <input type="number" className="w-full bg-white border-2 border-blue-500/20 rounded-xl p-2 font-black text-lg" value={editedCost} onChange={e => setEditedCost(e.target.value)} />
                  ) : (
                    <div className="text-xl font-black text-slate-800">{Math.round(item.cost_price).toLocaleString()} <span className="text-xs text-slate-400">sum</span></div>
                  )}
                </div>

                <div className="p-6 bg-slate-50 rounded-[30px] border border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Status & Verifikatsiya</p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${item.status === 'in_stock' ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.5)]'}`} />
                      <span className="font-black text-slate-700 uppercase text-xs tracking-tighter">{item.status === 'in_stock' ? 'Omborda' : 'Sotilgan'}</span>
                    </div>
                    {item.is_verified ? (
                      <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-[9px] font-black flex items-center gap-1">
                        <CheckCircle size={10}/> TEKSHIRILGAN
                      </span>
                    ) : (
                      <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-[9px] font-black flex items-center gap-1">
                        <AlertCircle size={10}/> TEKSHIRILMAGAN
                      </span>
                    )}
                  </div>
                </div>

                {item.status === 'sold' && (
                  <div className="p-6 bg-emerald-50 rounded-[30px] border border-emerald-100 group">
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">Sotuv Narxi</p>
                      <button onClick={() => setIsEditing(!isEditing)} className="text-emerald-600 opacity-0 group-hover:opacity-100 transition"><Edit2 size={14}/></button>
                    </div>
                    {isEditing ? (
                      <input type="number" className="w-full bg-white border-2 border-emerald-500/20 rounded-xl p-2 font-black text-lg" value={editedPrice} onChange={e => setEditedPrice(e.target.value)} />
                    ) : (
                      <div className="text-2xl font-black text-emerald-700">{Number(item.sale_price).toLocaleString()} <span className="text-xs opacity-50">sum</span></div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                   <div className="p-5 bg-slate-50 rounded-[25px] flex items-center gap-3">
                      <Calendar size={18} className="text-slate-400"/>
                      <div>
                        <p className="text-[8px] font-black text-slate-400 uppercase">Kirim</p>
                        <p className="text-[10px] font-bold text-slate-700">{new Date(item.imported_at).toLocaleDateString()}</p>
                      </div>
                   </div>
                   {item.expiry_date && (
                     <div className={`p-5 rounded-[25px] flex items-center gap-3 ${new Date(item.expiry_date) <= new Date() ? 'bg-rose-50 border border-rose-100' : 'bg-slate-50'}`}>
                        <Clock size={18} className={new Date(item.expiry_date) <= new Date() ? 'text-rose-500' : 'text-slate-400'}/>
                        <div>
                          <p className={`text-[8px] font-black uppercase ${new Date(item.expiry_date) <= new Date() ? 'text-rose-600' : 'text-slate-400'}`}>Muddati</p>
                          <p className={`text-[10px] font-bold ${new Date(item.expiry_date) <= new Date() ? 'text-rose-700' : 'text-slate-700'}`}>{new Date(item.expiry_date).toLocaleDateString()}</p>
                        </div>
                     </div>
                   )}
                </div>

                {item.sold_at && (
                  <div className="p-5 bg-slate-50 rounded-[25px] flex items-center gap-3">
                     <Clock size={18} className="text-slate-400"/>
                     <div>
                       <p className="text-[8px] font-black text-slate-400 uppercase">Sotuv</p>
                       <p className="text-[10px] font-bold text-slate-700">{new Date(item.sold_at).toLocaleString()}</p>
                     </div>
                  </div>
                )}
              </div>

              {isEditing ? (
                <button onClick={handleSave} className="w-full bg-emerald-600 text-white py-5 rounded-[30px] font-black text-lg shadow-xl flex items-center justify-center gap-3 hover:bg-emerald-700 transition active:scale-95">
                  <Save size={24}/> O'ZGARISHLARNI SAQLASH
                </button>
              ) : item.status === 'in_stock' ? (
                <button onClick={() => handleSellOne(item)} className="w-full bg-slate-900 text-white py-6 rounded-[30px] font-black text-lg hover:bg-blue-600 transition shadow-2xl flex items-center justify-center gap-3 active:scale-95">
                  <ShoppingCart size={28}/> SOTUVNI AMALGA OSHIRISH
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailModal;
